import { Button, Spinner, Text, Tooltip } from '@coreai/fluentui-react';
import { ArrowRepeatAllRegular, DismissRegular } from '@fluentui/react-icons';
import { RUN_RETRY_TOOLTIP } from '@/components/MonitoringView/shared/resubmit';
import { WorkflowRunsPanelStyles } from './WorkflowRunsPanel.styles';

type RunSelectionToolbarProps = {
  selectedCount: number;
  eligibleCount: number;
  isPending: boolean;
  onRetry: () => void;
  onClear: () => void;
};

export const RunSelectionToolbar = ({
  selectedCount,
  eligibleCount,
  isPending,
  onRetry,
  onClear,
}: RunSelectionToolbarProps) => {
  const styles = WorkflowRunsPanelStyles();
  const ineligibleCount = selectedCount - eligibleCount;
  const retryDisabled = isPending || eligibleCount === 0;
  let tooltip = RUN_RETRY_TOOLTIP;
  if (selectedCount === 0) tooltip = 'Select runs to retry.';
  else if (eligibleCount === 0) tooltip = 'None of the selected runs can be retried.';
  else if (ineligibleCount > 0) tooltip = `${RUN_RETRY_TOOLTIP} ${ineligibleCount} ineligible selected run${ineligibleCount === 1 ? '' : 's'} will be skipped.`;

  return (
    <div className={styles.selectionToolbar} role="toolbar" aria-label="Selected runs" data-testid="runs-selection-toolbar">
      <Text className={styles.selectionCount} aria-live="polite" data-testid="runs-selection-count">
        {selectedCount} selected
        {selectedCount > 0 && ineligibleCount > 0 && ` · ${eligibleCount} eligible`}
      </Text>
      <div className={styles.headerActions}>
        <Tooltip content={tooltip} relationship="description">
          <Button
            appearance="subtle"
            size="small"
            icon={isPending ? <Spinner size="extra-tiny" aria-hidden="true" /> : <ArrowRepeatAllRegular />}
            disabledFocusable={retryDisabled}
            onClick={retryDisabled ? undefined : onRetry}
            data-testid="runs-bulk-retry"
          >
            {isPending ? 'Retrying…' : 'Retry'}
          </Button>
        </Tooltip>
        <Tooltip content="Clear selection" relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={<DismissRegular />}
            disabled={selectedCount === 0 || isPending}
            onClick={onClear}
            aria-label="Clear selection"
            data-testid="runs-selection-clear"
          />
        </Tooltip>
      </div>
    </div>
  );
};
